import logger from '@/lib/logger';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { VideoPlayer } from '../VideoPlayer';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { testService } from '@/services/api/testService';
import { generateSampleCode } from '@/lib/utils/sampleCodeGenerator';
import { impedanceExecToJson } from '@/lib/protobuf';
import { useState } from 'react';
import { useHasRole } from '@/store';
import { ResearchParamsPanel } from './ResearchParamsPanel';

/**
 * Step 2: Apply Solution (Baseline Preparation)
 * 
 * Instruct user to apply solution (without pathogen) to electrode wells.
 * Creates test in backend before proceeding to baseline measurement.
 */
export function Step2ApplySolution() {
  const setCurrentStep = useMeasurementWizardStore((s) => s.setCurrentStep);
  const testId = useMeasurementWizardStore((s) => s.testId);
  const setTestId = useMeasurementWizardStore((s) => s.setTestId);
  const sampleCode = useMeasurementWizardStore((s) => s.sampleCode);
  const selectedProfile = useMeasurementWizardStore((s) => s.selectedProfile);
  const selectedElectrodeType = useMeasurementWizardStore((s) => s.selectedElectrodeType);
  const initialDeviceSnapshot = useMeasurementWizardStore((s) => s.initialDeviceSnapshot);
  const [isCreatingTest, setIsCreatingTest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isResearcher = useHasRole('researcher');

  const handleNext = async () => {
    try {
      setIsCreatingTest(true);
      setError(null);

      // Create test if not already created
      let currentTestId = testId;
      if (!currentTestId) {
        // Validate required fields
        if (!selectedProfile) {
          throw new Error('No profile selected. Please reload the wizard.');
        }
        if (!selectedElectrodeType) {
          throw new Error('No electrode type selected. Please reload the wizard.');
        }

        const testData = {
          profileId: selectedProfile.id,
          electrodeTypeId: selectedElectrodeType.id,
          sampleCode: sampleCode || generateSampleCode(),
          // Include initial device snapshot captured at wizard startup
          deviceState: initialDeviceSnapshot ? {
            batteryLevel: initialDeviceSnapshot.batteryLevel,
            batteryVoltage: initialDeviceSnapshot.batteryVoltage,
            temperature: initialDeviceSnapshot.temperature,
            humidity: initialDeviceSnapshot.humidity,
            powerState: initialDeviceSnapshot.powerState,
            electrodeType: initialDeviceSnapshot.electrodeType,
            isBusy: initialDeviceSnapshot.busy,
            progressPercent: initialDeviceSnapshot.progress,
            warnings: initialDeviceSnapshot.warnings,
          } : undefined,
          // If the operator tweaked parameters via ResearchParamsPanel, include the
          // modified ImpedanceExec so the profile snapshot records what was actually
          // sent to the device rather than the saved profile defaults.
          // Serialised as a JSON string to match the backend's string? field type.
          impedanceExecJsonOverride: selectedProfile.impedanceExecJson
            ? JSON.stringify(impedanceExecToJson(selectedProfile.impedanceExecJson))
            : undefined,
        };

        logger.debug('[Step2] Creating test:', testData);
        const testResponse = await testService.createTest(testData);
        currentTestId = testResponse.id;
        setTestId(currentTestId);
        logger.debug('[Step2] Test created with ID:', currentTestId);
        
        // Start the test (transition to 'running' status)
        logger.debug('[Step2] Starting test...');
        await testService.startTest(currentTestId);
        logger.debug('[Step2] Test started successfully');
      }

      // Proceed to Step 3 (baseline measurement)
      setCurrentStep(3);
    } catch (err: any) {
      logger.error('[Step2] Failed to create test:', err);
      setError(err.message || 'Failed to create test');
      // TODO: Add toast error notification
    } finally {
      setIsCreatingTest(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', py: 0 }}>
      <Typography variant="h5" gutterBottom align="center">
        Upper Respiratory Infection Panel
      </Typography>

      <Box sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <VideoPlayer
          src="/videos/step-2-apply-solution.mp4"
          poster="/videos/step-2-poster.jpg"
          controls
          autoPlay
        />
      </Box>

      <Typography variant="body1" sx={{ mb: 4 }}>
        Add exactly three drops of the solution from the white bottle 
        into the designated test well.
      </Typography>

      {isResearcher && <ResearchParamsPanel />}

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2, mt: 2 }}>
          Error: {error}
        </Typography>
      )}

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handleNext}
        disabled={isCreatingTest}
        startIcon={isCreatingTest ? <CircularProgress size={20} color="inherit" /> : undefined}
      >
        {isCreatingTest ? 'Creating test...' : 'Next'}
      </Button>
    </Box>
  );
}
